import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { Input, Dropdown } from '@components';
import { useTimezone } from '@hooks';
import { timezoneOptions, findMatchingTimezone } from '@utilities';
import { useAppContext } from '@molecules';

const CAMPAIGN_ID_RE = /\/campaign_monitoring\/([^/]+)|\/campaigns\/([^/]+)\//;

const findMatchingTimezoneOption = (timezone: string): OptionType | undefined =>
  timezoneOptions.find(o => o.value === findMatchingTimezone(timezone));

type TimezoneOption = OptionType & {
  variant?: 'preset' | 'timezone';
};

const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const getOptionSearchText = (option: TimezoneOption) => {
  return [option.value, option.valueLabel, option.label]
    .filter(value => typeof value === 'string')
    .join(' ')
    .toLowerCase();
};

const createPresetOption = (
  title: string,
  timezone: string
): TimezoneOption => {
  const matched = findMatchingTimezoneOption(timezone);
  const resolvedValue = (matched?.value as string) ?? timezone;
  const timezoneLabel =
    typeof matched?.label === 'string' ? matched.label : timezone;

  return {
    value: resolvedValue,
    valueLabel: title,
    label: (
      <div className="flex flex-col">
        <span>{title}</span>
        <span className="text-tiny text-textLight">{timezoneLabel}</span>
      </div>
    ),
    variant: 'preset',
  };
};

const TimezoneDropdown: React.FC = () => {
  const { meta } = useAppContext();
  const { pathname } = useLocation();
  const [timezone, changeTimezone] = useTimezone();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const urlMatch = pathname.match(CAMPAIGN_ID_RE);
  const urlCampaignId = urlMatch?.[1] ?? urlMatch?.[2];
  const campaignId = urlCampaignId || meta.initialCampaignFilter;
  const campaign = campaignId ? meta.campaignsMap[campaignId] : undefined;
  const campaignTimezone = campaign?.default_timezone;
  const campaignName = String(
    campaign?.name ||
      meta.campaigns.find(c => c.value === campaignId)?.label ||
      'Campaign'
  );

  const options = useMemo<TimezoneOption[]>(() => {
    const presets: TimezoneOption[] = [
      createPresetOption('Browser timezone', browserTimezone),
    ];

    if (campaignTimezone) {
      presets.push(createPresetOption(campaignName, campaignTimezone));
    }

    const presetValues = new Set(presets.map(option => option.value));

    return [
      ...presets,
      ...timezoneOptions
        .filter(option => !presetValues.has(option.value))
        .map(option => ({
          ...option,
          variant: 'timezone' as const,
        })),
    ];
  }, [campaignTimezone, campaignName]);

  const filteredOptions = useMemo(
    () =>
      search
        ? options.filter(o =>
            getOptionSearchText(o).includes(search.toLowerCase())
          )
        : options,
    [options, search]
  );

  const handleToggle = (open: boolean) => {
    setIsOpen(open);
    if (!open) setSearch('');
  };

  const handleChange = (tz: string) => {
    changeTimezone(tz);
    setSearch('');
  };

  return (
    <Dropdown
      value={timezone}
      open={isOpen}
      onToggle={handleToggle}
      options={filteredOptions}
      onChange={handleChange}
      grow={false}
      popupClassName="w-[28rem] max-h-[28rem] overflow-y-auto rounded-sm"
      renderOption={({ index, option, options, value, handleSelect }) => {
        const timezoneOption = option as TimezoneOption;
        const prevVariant =
          index > 0 ? (options[index - 1] as TimezoneOption).variant : null;

        const showPresetHeader =
          !search && index === 0 && timezoneOption.variant === 'preset';
        const showTimezoneHeader =
          !search &&
          timezoneOption.variant === 'timezone' &&
          prevVariant === 'preset';

        return (
          <React.Fragment key={String(option.value)}>
            {showPresetHeader && (
              <div className="px-sm pb-xs text-tiny font-medium text-textLight uppercase tracking-wide">
                Presets
              </div>
            )}
            {showTimezoneHeader && (
              <div className="flex items-center gap-x-sm px-sm py-xs">
                <div className="h-px flex-1 bg-active" />
                <span className="text-tiny font-medium text-textLight uppercase tracking-wide shrink-0">
                  All timezones
                </span>
                <div className="h-px flex-1 bg-active" />
              </div>
            )}
            <div
              onClick={e => {
                e.stopPropagation();
                handleSelect(option.value);
              }}
              className={[
                'rounded-sm hover:bg-active px-sm py-xs transition-all duration-150',
                'truncate shrink-0 cursor-pointer',
                option.value === value ? 'bg-active text-text' : 'text-text',
              ].join(' ')}
            >
              {option.label}
            </div>
          </React.Fragment>
        );
      }}
      renderValue={({ label }) => {
        const displayValue = typeof label === 'string' ? label : timezone;

        return isOpen ? (
          <Input
            isChild
            withBorder
            autoFocus
            inputClassName="[field-sizing:content] min-w-[20rem] pr-10"
            value={search}
            placeholder={displayValue}
            onChange={setSearch}
          />
        ) : (
          <Input
            isChild
            withBorder
            readonly
            inputClassName="[field-sizing:content] min-w-[20rem] pr-10"
            value={displayValue}
            placeholder="Timezone"
          />
        );
      }}
    />
  );
};

export default React.memo(TimezoneDropdown);
